import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  TextInput,
  TouchableOpacity,
  ScrollView,
  StyleSheet,
  Dimensions,
  Platform,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useTheme } from '../contexts/ThemeContext';
import Footer from '../components/Footer';
import { formatCurrency, formatNumber, formatIndianNumber } from '../utils/formatters';

const { width } = Dimensions.get('window');

const PFCalculatorScreen = ({ navigation }) => {
  const { colors, isDarkMode } = useTheme();
  
  // State variables
  const [results, setResults] = useState(null);
  const [showFAQ, setShowFAQ] = useState(true);
  const [showFrequencyDropdown, setShowFrequencyDropdown] = useState(false);

  // Screen dimensions for responsive design
  const [screenData, setScreenData] = useState(Dimensions.get('window'));
  const isWeb = Platform.OS === 'web';
  const isMobile = screenData.width < 900;

  // Update screen dimensions on resize
  useEffect(() => {
    const subscription = Dimensions.addEventListener('change', ({ window }) => {
      setScreenData(window);
    });
    return () => subscription?.remove();
  }, []);
  
  const [sidebarVisible, setSidebarVisible] = useState(false);
  const [isCalculated, setIsCalculated] = useState(false);
  
  const [formData, setFormData] = useState({
    monthlyInvestment: '10000', // Monthly PF Investment
    interestRate: '8.25', // Current PF Interest Rate (FY 2025-26)
    duration: '15', // Duration of investment (15-50 YEARS)
    frequency: 'Monthly', // Frequency of Investment
  });

  // Calculate PF maturity amount
  const calculatePF = () => {
    const monthlyAmount = parseFloat(formData.monthlyInvestment) || 0;
    const annualRate = parseFloat(formData.interestRate) / 100;
    const years = parseFloat(formData.duration) || 0;
    
    let totalAmount = 0;
    let totalInvestment = 0;
    
    if (formData.frequency === 'Monthly') {
      const monthlyRate = annualRate / 12;
      const totalMonths = years * 12;
      totalInvestment = monthlyAmount * totalMonths;
      
      // Future Value of Annuity formula
      if (monthlyRate > 0) {
        totalAmount = monthlyAmount * (((1 + monthlyRate) ** totalMonths - 1) / monthlyRate);
      } else {
        totalAmount = totalInvestment;
      }
    } else if (formData.frequency === 'Yearly') {
      const yearlyAmount = monthlyAmount * 12;
      totalInvestment = yearlyAmount * years;
      
      if (annualRate > 0) {
        totalAmount = yearlyAmount * (((1 + annualRate) ** years - 1) / annualRate);
      } else {
        totalAmount = totalInvestment;
      }
    }
    
    const interestEarned = totalAmount - totalInvestment;
    
    setResults({
      maturityAmount: totalAmount,
      totalInvestment: totalInvestment,
      interestEarned: interestEarned,
    });
    setIsCalculated(true);
  };

  // Auto-calculate when form data changes
  useEffect(() => {
    calculatePF();
  }, [formData]);

  const renderHeader = () => (
    <View style={styles.header}>
      {isMobile && (
        <TouchableOpacity 
          style={styles.hamburgerButton}
          onPress={() => setSidebarVisible(!sidebarVisible)}
        >
          <Ionicons name="menu" size={24} color="white" />
        </TouchableOpacity>
      )}
      <Text style={styles.headerTitle}>PF Calculator</Text>
      <Text style={styles.headerSubtitle}>
        Calculate your Provident Fund maturity amount with our comprehensive PF calculator. 
        Plan your retirement savings and understand how your PF contributions grow over time with compound interest.
      </Text>
      
      <View style={styles.headerTags}>
        <View style={styles.headerTag}>
          <Text style={styles.headerTagText}>PF Maturity</Text>
        </View>
        <View style={styles.headerTag}>
          <Text style={styles.headerTagText}>Interest Calculation</Text>
        </View>
        <View style={styles.headerTag}>
          <Text style={styles.headerTagText}>Retirement Planning</Text>
        </View>
        <View style={styles.headerTag}>
          <Text style={styles.headerTagText}>100% Free</Text>
        </View>
      </View>
    </View>
  );

  const renderMonthlyInvestmentInput = () => (
    <View style={styles.inputGroup}>
      <Text style={styles.label}>Monthly PF Investment</Text>
      <View style={styles.inputContainer}>
        <Text style={styles.currencySymbol}>₹</Text>
        <TextInput
          style={styles.input}
          value={formData.monthlyInvestment}
          onChangeText={(value) => setFormData({...formData, monthlyInvestment: value})}
          placeholder="10000"
          keyboardType="numeric"
          placeholderTextColor={colors.textSecondary}
        />
      </View>
    </View>
  );

  const renderInterestRateInput = () => (
    <View style={styles.inputGroup}>
      <Text style={styles.label}>Current PF Interest Rate (FY 2025-26)</Text>
      <View style={styles.inputContainer}>
        <TextInput
          style={styles.input}
          value={formData.interestRate}
          onChangeText={(value) => setFormData({...formData, interestRate: value})}
          placeholder="8.25"
          keyboardType="numeric"
          placeholderTextColor={colors.textSecondary}
        />
        <Text style={styles.percentSymbol}>%</Text>
      </View>
    </View>
  );

  const renderDurationInput = () => (
    <View style={styles.inputGroup}>
      <Text style={styles.label}>Duration of Investment (15-50 YEARS)</Text>
      <View style={styles.inputContainer}>
        <TextInput
          style={styles.input}
          value={formData.duration}
          onChangeText={(value) => setFormData({...formData, duration: value})}
          placeholder="15"
          keyboardType="numeric"
          placeholderTextColor={colors.textSecondary}
        />
        <Text style={styles.percentSymbol}>Yrs</Text>
      </View>
    </View>
  );

  const renderFrequencyDropdown = () => {
    const frequencies = ['Monthly', 'Yearly'];
    
    return (
      <View style={styles.inputGroup}>
        <Text style={styles.label}>Frequency of Investment</Text>
        <TouchableOpacity
          style={styles.dropdownContainer}
          onPress={() => setShowFrequencyDropdown(!showFrequencyDropdown)}
        >
          <Text style={styles.dropdownText}>{formData.frequency}</Text>
          <Ionicons 
            name={showFrequencyDropdown ? "chevron-up" : "chevron-down"} 
            size={20} 
            color={colors.text} 
          />
        </TouchableOpacity>
        
        {showFrequencyDropdown && (
          <View style={styles.dropdownMenu}>
            {frequencies.map((freq) => (
              <TouchableOpacity
                key={freq}
                style={styles.dropdownItem}
                onPress={() => {
                  setFormData({...formData, frequency: freq});
                  setShowFrequencyDropdown(false);
                }}
              >
                <Text style={styles.dropdownItemText}>{freq}</Text>
              </TouchableOpacity>
            ))}
          </View>
        )}
      </View>
    );
  };

  const renderCalculator = () => (
    <View style={styles.calculatorContainer}>
      <View style={styles.calculatorRow}>
        <View style={styles.formContent}>
          {renderMonthlyInvestmentInput()}
          {renderInterestRateInput()}
          {renderDurationInput()}
          {renderFrequencyDropdown()}
        </View>
        
        {!isMobile && (
          <View style={styles.summaryPanel}>
            <View style={styles.panelHeader}>
              <Text style={styles.panelTitle}>PF Maturity Amount</Text>
            </View>
            
            <View style={styles.resultCard}>
              <Text style={styles.resultLabel}>Maturity Amount</Text>
              <Text style={styles.resultValue}>
                ₹{results ? formatIndianNumber(Math.round(results.maturityAmount)) : '35,96,445.5'}
              </Text>
            </View>
            
            <View style={styles.resultCard}>
              <Text style={styles.resultLabel}>Total Investment</Text>
              <Text style={styles.resultAmount}>
                ₹{results ? formatIndianNumber(Math.round(results.totalInvestment)) : '18,00,000'}
              </Text>
            </View>
            
            <View style={styles.resultCard}>
              <Text style={styles.resultLabel}>Interest Earned</Text>
              <Text style={styles.resultAmount}>
                ₹{results ? formatIndianNumber(Math.round(results.interestEarned)) : '17,96,445.5'}
              </Text>
            </View>
            
            <View style={styles.infoBox}>
              <Text style={styles.infoText}>
                Your PF will grow to ₹{results ? formatIndianNumber(Math.round(results.maturityAmount)) : '35,96,445.5'} 
                after {formData.duration} years of investment.
              </Text>
            </View>
          </View>
        )}
      </View>
    </View>
  );

  const renderResults = () => {
    if (isMobile && results) {
      return (
        <View style={styles.resultsContainer}>
          <Text style={styles.sectionTitle}>PF Maturity Amount</Text>
          
          <View style={styles.resultCard}>
            <Text style={styles.resultLabel}>Maturity Amount</Text>
            <Text style={styles.resultValue}>
              ₹{formatIndianNumber(Math.round(results.maturityAmount))}
            </Text>
          </View>
          
          <View style={styles.resultCard}>
            <Text style={styles.resultLabel}>Total Investment</Text>
            <Text style={styles.resultAmount}>
              ₹{formatIndianNumber(Math.round(results.totalInvestment))}
            </Text>
          </View>
          
          <View style={styles.resultCard}>
            <Text style={styles.resultLabel}>Interest Earned</Text>
            <Text style={styles.resultAmount}>
              ₹{formatIndianNumber(Math.round(results.interestEarned))}
            </Text>
          </View>
          
          <View style={styles.infoBox}>
            <Text style={styles.infoText}>
              Your PF will grow to ₹{formatIndianNumber(Math.round(results.maturityAmount))} 
              after {formData.duration} years of investment.
            </Text>
          </View>
        </View>
      );
    }
    return null;
  };

  const renderPFInformation = () => (
    <View style={styles.infoSection}>
      <Text style={styles.sectionTitle}>What is PF Calculator?</Text>
      <Text style={styles.sectionText}>
        A Provident Fund (PF) calculator is a financial tool that helps you estimate the maturity amount 
        of your PF account. It calculates how your monthly contributions will grow over time with compound interest, 
        helping you plan for retirement and understand the long-term benefits of PF investments.
      </Text>
      
      <Text style={styles.sectionSubtitle}>Key Features:</Text>
      <View style={styles.featuresList}>
        <Text style={styles.featureItem}>• Calculate PF maturity amount based on monthly contributions</Text>
        <Text style={styles.featureItem}>• Current PF interest rate for FY 2025-26 (8.25%)</Text>
        <Text style={styles.featureItem}>• Flexible investment duration (15-50 years)</Text>
        <Text style={styles.featureItem}>• Monthly or yearly contribution frequency</Text>
        <Text style={styles.featureItem}>• Detailed breakdown of total investment vs interest earned</Text>
      </View>
    </View>
  );

  const renderFAQSection = () => (
    <View style={styles.faqContainer}>
      <TouchableOpacity 
        style={styles.faqHeader}
        onPress={() => setShowFAQ(!showFAQ)}
      >
        <Text style={styles.faqTitle}>Frequently Asked Questions</Text>
        <Ionicons 
          name={showFAQ ? "chevron-up" : "chevron-down"} 
          size={24} 
          color={colors.text} 
        />
      </TouchableOpacity>
      
      {showFAQ && (
        <View style={styles.faqContent}>
          <View style={styles.faqItem}>
            <Text style={styles.faqQuestion}>What is the current PF interest rate?</Text>
            <Text style={styles.faqAnswer}>
              The current PF interest rate for FY 2025-26 is 8.25% per annum, as set by the EPFO.
            </Text>
          </View>
          
          <View style={styles.faqItem}>
            <Text style={styles.faqQuestion}>How is PF interest calculated?</Text>
            <Text style={styles.faqAnswer}>
              PF interest is calculated on a monthly basis using compound interest. The interest is credited 
              to your account annually, but the calculation considers monthly contributions.
            </Text>
          </View>
          
          <View style={styles.faqItem}>
            <Text style={styles.faqQuestion}>What is the minimum and maximum PF contribution?</Text>
            <Text style={styles.faqAnswer}>
              The minimum PF contribution is 12% of basic salary, and there's a ceiling of ₹1,800 per month 
              for the employer's contribution (on basic salary up to ₹15,000).
            </Text>
          </View>
        </View>
      )}
    </View>
  );

  const renderSEOContent = () => (
    <View style={styles.infoSection}>
      <Text style={styles.seoTitle}>Complete Guide to Provident Fund (PF) Investment</Text>
      <Text style={styles.seoSubtitle}>Master PF calculations and maximize your retirement savings</Text>
      
      <View style={styles.seoSection}>
        <Text style={styles.seoSectionTitle}>Understanding Provident Fund (PF)</Text>
        <Text style={styles.seoText}>
          The Provident Fund (PF) is a retirement savings scheme managed by the Employees' Provident Fund Organisation (EPFO). 
          It's a mandatory contribution scheme where both employee and employer contribute 12% of the basic salary towards the fund. 
          The accumulated amount with interest is paid to the employee upon retirement, resignation, or withdrawal.
        </Text>
        <Text style={styles.seoText}>
          PF serves as a long-term investment vehicle that provides financial security during retirement. The fund earns 
          interest annually, which is declared by the EPFO based on market conditions and government policies.
        </Text>
      </View>

      <View style={styles.seoSection}>
        <Text style={styles.seoSectionTitle}>Key PF Calculator Features</Text>
        <View style={styles.featuresList}>
          <View style={styles.featureItem}>
            <Text style={styles.featureText}>• Accurate maturity amount calculation based on current interest rates</Text>
          </View>
          <View style={styles.featureItem}>
            <Text style={styles.featureText}>• Monthly and yearly contribution frequency options</Text>
          </View>
          <View style={styles.featureItem}>
            <Text style={styles.featureText}>• Flexible investment duration from 15 to 50 years</Text>
          </View>
          <View style={styles.featureItem}>
            <Text style={styles.featureText}>• Real-time interest rate updates (currently 8.25% for FY 2025-26)</Text>
          </View>
          <View style={styles.featureItem}>
            <Text style={styles.featureText}>• Detailed breakdown of contributions vs interest earned</Text>
          </View>
          <View style={styles.featureItem}>
            <Text style={styles.featureText}>• Mobile-responsive design for calculations on any device</Text>
          </View>
        </View>
      </View>

      <View style={styles.seoSection}>
        <Text style={styles.seoSectionTitle}>PF vs Other Retirement Schemes</Text>
        <View style={styles.comparisonContainer}>
          <View>
            <Text style={styles.comparisonTitle}>PF vs PPF</Text>
            <Text style={styles.comparisonText}>• PF: Mandatory for salaried employees, 12% contribution</Text>
            <Text style={styles.comparisonText}>• PPF: Voluntary scheme, up to ₹1.5 lakh annual investment</Text>
            <Text style={styles.comparisonText}>• PF: Withdrawable on job change, PPF: 15-year lock-in</Text>
          </View>
          <View>
            <Text style={styles.comparisonTitle}>PF vs NPS</Text>
            <Text style={styles.comparisonText}>• PF: Fixed interest rate, guaranteed returns</Text>
            <Text style={styles.comparisonText}>• NPS: Market-linked returns, higher risk-reward</Text>
            <Text style={styles.comparisonText}>• PF: Tax-free withdrawal, NPS: Partial tax on withdrawal</Text>
          </View>
        </View>
      </View>

      <View style={styles.seoSection}>
        <Text style={styles.seoSectionTitle}>How to Use the PF Calculator</Text>
        <View style={styles.stepsList}>
          <View style={styles.stepItem}>
            <View style={styles.stepNumber}>
              <Text style={styles.stepNumberText}>1</Text>
            </View>
            <Text style={styles.stepText}>Enter your monthly PF contribution amount (typically 12% of basic salary)</Text>
          </View>
          <View style={styles.stepItem}>
            <View style={styles.stepNumber}>
              <Text style={styles.stepNumberText}>2</Text>
            </View>
            <Text style={styles.stepText}>Set the current PF interest rate (8.25% for FY 2025-26)</Text>
          </View>
          <View style={styles.stepItem}>
            <View style={styles.stepNumber}>
              <Text style={styles.stepNumberText}>3</Text>
            </View>
            <Text style={styles.stepText}>Choose your investment duration (years until retirement)</Text>
          </View>
          <View style={styles.stepItem}>
            <View style={styles.stepNumber}>
              <Text style={styles.stepNumberText}>4</Text>
            </View>
            <Text style={styles.stepText}>Select contribution frequency (monthly or yearly)</Text>
          </View>
          <View style={styles.stepItem}>
            <View style={styles.stepNumber}>
              <Text style={styles.stepNumberText}>5</Text>
            </View>
            <Text style={styles.stepText}>View your calculated maturity amount and interest earned</Text>
          </View>
        </View>
      </View>

      <View style={styles.seoSection}>
        <Text style={styles.seoSectionTitle}>PF Optimization Strategies</Text>
        <View style={styles.strategiesList}>
          <View style={styles.strategyItem}>
            <View style={styles.strategyContent}>
              <Text style={styles.strategyTitle}>Maximize Contributions</Text>
              <Text style={styles.strategyText}>
                Consider voluntary PF contributions beyond the mandatory 12% to accelerate your retirement corpus growth.
              </Text>
            </View>
          </View>
          <View style={styles.strategyItem}>
            <View style={styles.strategyContent}>
              <Text style={styles.strategyTitle}>Avoid Premature Withdrawals</Text>
              <Text style={styles.strategyText}>
                Resist withdrawing PF for non-essential purposes to maintain the power of compound interest.
              </Text>
            </View>
          </View>
          <View style={styles.strategyItem}>
            <View style={styles.strategyContent}>
              <Text style={styles.strategyTitle}>Transfer PF Accounts</Text>
              <Text style={styles.strategyText}>
                Always transfer your PF account when changing jobs to maintain continuity and avoid withdrawal penalties.
              </Text>
            </View>
          </View>
          <View style={styles.strategyItem}>
            <View style={styles.strategyContent}>
              <Text style={styles.strategyTitle}>Monitor Interest Rates</Text>
              <Text style={styles.strategyText}>
                Stay updated with annual PF interest rate declarations to adjust your retirement planning accordingly.
              </Text>
            </View>
          </View>
        </View>
      </View>

      <View style={styles.seoSection}>
        <Text style={styles.seoSectionTitle}>PF Tax Benefits and Compliance</Text>
        <Text style={styles.seoText}>
          PF contributions enjoy triple tax benefits under the EEE (Exempt-Exempt-Exempt) category:
        </Text>
        <View style={styles.featuresList}>
          <View style={styles.featureItem}>
            <Text style={styles.featureText}>• Contributions are tax-deductible under Section 80C (up to ₹1.5 lakh)</Text>
          </View>
          <View style={styles.featureItem}>
            <Text style={styles.featureText}>• Interest earned is tax-free during accumulation</Text>
          </View>
          <View style={styles.featureItem}>
            <Text style={styles.featureText}>• Maturity amount is completely tax-free after 5 years of service</Text>
          </View>
          <View style={styles.featureItem}>
            <Text style={styles.featureText}>• Partial withdrawals for specific purposes are allowed tax-free</Text>
          </View>
        </View>
      </View>

      <View style={styles.seoSection}>
        <Text style={styles.seoSectionTitle}>Common PF Mistakes to Avoid</Text>
        <View style={styles.featuresList}>
          <View style={styles.featureItem}>
            <Text style={styles.featureText}>• Withdrawing PF for non-essential expenses</Text>
          </View>
          <View style={styles.featureItem}>
            <Text style={styles.featureText}>• Not transferring PF account when changing jobs</Text>
          </View>
          <View style={styles.featureItem}>
            <Text style={styles.featureText}>• Ignoring annual PF statements and interest credits</Text>
          </View>
          <View style={styles.featureItem}>
            <Text style={styles.featureText}>• Not updating nominee details regularly</Text>
          </View>
          <View style={styles.featureItem}>
            <Text style={styles.featureText}>• Failing to link Aadhaar and PAN with PF account</Text>
          </View>
        </View>
      </View>

      <View style={styles.seoSection}>
        <Text style={styles.seoSectionTitle}>PF Success Examples</Text>
        <Text style={styles.seoText}>
          <Text style={styles.comparisonTitle}>Example 1: Early Career Professional</Text>
          {'\n'}A 25-year-old with ₹8,000 monthly PF contribution for 35 years at 8.25% interest will accumulate 
          approximately ₹2.1 crores by retirement, with ₹33.6 lakhs in contributions and ₹1.76 crores in interest.
        </Text>
        <Text style={styles.seoText}>
          <Text style={styles.comparisonTitle}>Example 2: Mid-Career Switch</Text>
          {'\n'}A 35-year-old with ₹15,000 monthly contribution for 25 years will build a corpus of ₹1.8 crores, 
          demonstrating that even mid-career PF planning can create substantial retirement wealth.
        </Text>
      </View>
    </View>
  );

  const styles = StyleSheet.create({
    container: {
      flex: 1,
      backgroundColor: colors.background,
    },
    header: {
      backgroundColor: colors.primary,
      paddingHorizontal: 20,
      paddingVertical: 40,
      paddingTop: Platform.OS === 'web' && Dimensions.get('window').width >= 900 ? 40 : 60,
    },
    hamburgerButton: {
      position: 'absolute',
      top: 20,
      left: 20,
      zIndex: 1,
      padding: 8,
    },
    headerTitle: {
      fontSize: Dimensions.get('window').width < 900 ? 24 : 32,
      fontWeight: 'bold',
      color: colors.background,
      marginBottom: 12,
      textAlign: Dimensions.get('window').width < 900 ? 'center' : 'left',
    },
    headerSubtitle: {
      fontSize: 16,
      color: colors.background + 'E6',
      lineHeight: 24,
      marginBottom: 20,
      textAlign: Dimensions.get('window').width < 900 ? 'center' : 'left',
    },
    headerTags: {
      flexDirection: 'row',
      flexWrap: 'wrap',
      gap: 8,
      justifyContent: Dimensions.get('window').width < 900 ? 'center' : 'flex-start',
    },
    headerTag: {
      backgroundColor: colors.background + '33',
      paddingHorizontal: 12,
      paddingVertical: 6,
      borderRadius: 16,
    },
    headerTagText: {
      color: colors.background,
      fontSize: 12,
      fontWeight: '500',
    },
    calculatorContainer: {
      backgroundColor: colors.background,
      margin: 0,
      padding: 20,
    },
    formContent: {
      gap: 20,
      flex: 0.7,
    },
    calculatorRow: {
      flexDirection: 'row',
      gap: 24,
    },
    summaryPanel: {
      flex: 0.3,
      backgroundColor: colors.surface,
      borderRadius: 12,
      padding: 20,
      height: 'fit-content',
      borderWidth: 1,
      borderColor: colors.border,
    },
    panelHeader: {
      marginBottom: 20,
    },
    panelTitle: {
      fontSize: 18,
      fontWeight: 'bold',
      color: colors.text,
    },
    inputGroup: {
      marginBottom: 20,
    },
    label: {
      fontSize: 16,
      fontWeight: '600',
      color: colors.text,
      marginBottom: 8,
    },
    inputContainer: {
      flexDirection: 'row',
      alignItems: 'center',
      backgroundColor: colors.cardBackground,
      borderRadius: 8,
      borderWidth: 1,
      borderColor: colors.border,
      paddingHorizontal: 12,
      height: 48,
    },
    currencySymbol: {
      fontSize: 16,
      color: colors.textSecondary,
      marginRight: 8,
    },
    percentSymbol: {
      fontSize: 16,
      color: colors.textSecondary,
      marginLeft: 8,
    },
    input: {
      flex: 1,
      fontSize: 16,
      color: colors.text,
      borderWidth: 0,
    },
    dropdownContainer: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      backgroundColor: colors.cardBackground,
      borderRadius: 8,
      borderWidth: 1,
      borderColor: colors.border,
      paddingHorizontal: 12,
      height: 48,
    },
    dropdownText: {
      fontSize: 16,
      color: colors.text,
    },
    dropdownMenu: {
      backgroundColor: colors.cardBackground,
      borderRadius: 8,
      borderWidth: 1,
      borderColor: colors.border,
      marginTop: 4,
      elevation: 3,
      shadowColor: '#000',
      shadowOffset: { width: 0, height: 2 },
      shadowOpacity: 0.1,
      shadowRadius: 4,
    },
    dropdownItem: {
      padding: 12,
      borderBottomWidth: 1,
      borderBottomColor: colors.border,
    },
    dropdownItemText: {
      fontSize: 16,
      color: colors.text,
    },
    resultCard: {
      backgroundColor: colors.cardBackground,
      padding: 16,
      borderRadius: 8,
      marginBottom: 12,
      borderWidth: 1,
      borderColor: colors.border,
    },
    resultLabel: {
      fontSize: 14,
      color: colors.textSecondary,
      marginBottom: 4,
    },
    resultValue: {
      fontSize: 24,
      fontWeight: 'bold',
      color: colors.success,
    },
    resultAmount: {
      fontSize: 18,
      fontWeight: '600',
      color: colors.text,
    },
    infoBox: {
      backgroundColor: colors.primary + '20',
      padding: 12,
      borderRadius: 8,
      borderLeftWidth: 4,
      borderLeftColor: colors.primary,
    },
    infoText: {
      fontSize: 14,
      color: colors.primary,
      lineHeight: 20,
    },
    resultsContainer: {
      margin: 20,
    },
    sectionTitle: {
      fontSize: 20,
      fontWeight: 'bold',
      color: colors.text,
      marginBottom: 16,
    },
    infoSection: {
      margin: 20,
      backgroundColor: colors.surface,
      padding: 20,
      borderRadius: 12,
      elevation: 2,
      shadowColor: '#000',
      shadowOffset: { width: 0, height: 2 },
      shadowOpacity: 0.1,
      shadowRadius: 4,
    },
    sectionText: {
      fontSize: 16,
      color: colors.textSecondary,
      lineHeight: 24,
      marginBottom: 16,
    },
    sectionSubtitle: {
      fontSize: 18,
      fontWeight: '600',
      color: colors.text,
      marginBottom: 12,
    },
    featuresList: {
      marginLeft: 8,
    },
    featureItem: {
      fontSize: 16,
      color: colors.textSecondary,
      lineHeight: 24,
      marginBottom: 8,
    },
    faqContainer: {
      backgroundColor: colors.surface,
      margin: 20,
      borderRadius: 12,
      overflow: 'hidden',
      elevation: 2,
      shadowColor: '#000',
      shadowOffset: { width: 0, height: 2 },
      shadowOpacity: 0.1,
      shadowRadius: 4,
    },
    faqHeader: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'center',
      padding: 20,
      backgroundColor: colors.background,
    },
    faqTitle: {
      fontSize: 20,
      fontWeight: 'bold',
      color: colors.text,
    },
    faqContent: {
      padding: 20,
    },
    faqItem: {
      marginBottom: 20,
    },
    faqQuestion: {
      fontSize: 16,
      fontWeight: '600',
      color: colors.text,
      marginBottom: 8,
    },
    faqAnswer: {
      fontSize: 16,
      color: colors.textSecondary,
      lineHeight: 24,
    },
    // SEO Content Styles
    seoTitle: {
      fontSize: 24,
      fontWeight: 'bold',
      color: colors.text,
      marginBottom: 8,
      textAlign: 'center',
    },
    seoSubtitle: {
      fontSize: 18,
      color: colors.textSecondary,
      marginBottom: 20,
      textAlign: 'center',
    },
    seoText: {
      fontSize: 16,
      color: colors.textSecondary,
      lineHeight: 24,
      marginBottom: 16,
    },
    seoSection: {
      marginBottom: 24,
    },
    seoSectionTitle: {
      fontSize: 18,
      fontWeight: 'bold',
      color: colors.text,
      marginBottom: 12,
    },
    comparisonContainer: {
      gap: 16,
    },
    comparisonTitle: {
      fontSize: 16,
      fontWeight: 'bold',
      color: colors.primary,
      marginBottom: 8,
    },
    comparisonText: {
      fontSize: 14,
      color: colors.textSecondary,
      marginBottom: 4,
    },
    featureText: {
      fontSize: 14,
      color: colors.textSecondary,
      lineHeight: 20,
    },
    stepsList: {
      gap: 16,
    },
    stepItem: {
      flexDirection: 'row',
      alignItems: 'flex-start',
      gap: 12,
    },
    stepNumber: {
      width: 28,
      height: 28,
      borderRadius: 14,
      backgroundColor: colors.primary,
      justifyContent: 'center',
      alignItems: 'center',
    },
    stepNumberText: {
      color: colors.background,
      fontSize: 14,
      fontWeight: 'bold',
    },
    stepText: {
      flex: 1,
      fontSize: 14,
      color: colors.textSecondary,
      lineHeight: 20,
    },
    strategiesList: {
      gap: 16,
    },
    strategyItem: {
      backgroundColor: colors.cardBackground,
      borderRadius: 8,
      padding: 16,
      borderLeftWidth: 4,
      borderLeftColor: colors.primary,
      elevation: 1,
      shadowColor: '#000',
      shadowOffset: { width: 0, height: 1 },
      shadowOpacity: 0.1,
      shadowRadius: 2,
    },
    strategyContent: {
      gap: 8,
    },
    strategyTitle: {
      fontSize: 16,
      fontWeight: 'bold',
      color: colors.text,
    },
    strategyText: {
      fontSize: 14,
      color: colors.textSecondary,
      lineHeight: 20,
    },
  });

  return (
    <ScrollView style={styles.container} showsVerticalScrollIndicator={false}>
      {renderHeader()}
      {renderCalculator()}
      {renderResults()}
      {renderPFInformation()}
      {renderSEOContent()}
      {renderFAQSection()}
      <Footer />
    </ScrollView>
  );
};

export default PFCalculatorScreen;